import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../context/LanguageContext";
import SimulationResultsTable, { SimulationResults } from "../components/fts/SimulationResultsTable";
import { ROIPanel, EconomicImpact } from "../components/fts/ROIPanel";
import { simulationsService, Simulation } from "../services/simulations";
import {
    Play, ArrowLeft, RefreshCw, Download, Settings,
    CheckCircle2, Clock, AlertCircle, Loader2,
    Layers, Zap, Gauge, Building2, FileText, TrendingUp, Film,
    BarChart3
} from "lucide-react";

// --- Error Boundary for simulation result charts ---
class SimulationErrorBoundary extends React.Component<
    { children: React.ReactNode; onRetry?: () => void; isDark?: boolean },
    { hasError: boolean; errorMessage: string }
> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false, errorMessage: '' };
    }
    static getDerivedStateFromError(error: Error) {
        return { hasError: true, errorMessage: error.message };
    }
    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error('[SimulationErrorBoundary]', error, info);
    }
    render() {
        if (this.state.hasError) {
            const bg = this.props.isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200';
            const text = this.props.isDark ? 'text-red-300' : 'text-red-700';
            return (
                <div className={`p-8 rounded-xl border text-center ${bg}`}>
                    <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${text}`} />
                    <h3 className={`text-lg font-bold mb-2 ${text}`}>Error al renderizar resultados</h3>
                    <p className={`text-sm mb-4 ${text} opacity-70`}>{this.state.errorMessage}</p>
                    {this.props.onRetry && (
                        <button
                            onClick={() => {
                                this.setState({ hasError: false, errorMessage: '' });
                                this.props.onRetry?.();
                            }}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-red-500 hover:bg-red-600 text-white font-bold transition-colors"
                        >
                            <RefreshCw size={18} />
                            Reintentar
                        </button>
                    )}
                </div>
            );
        }
        return this.props.children;
    }
}

const SimulationDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { language } = useLanguage();

    const [simulation, setSimulation] = useState<Simulation | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRunning, setIsRunning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState<number>(0);
    const [progressMessage, setProgressMessage] = useState<string>('');

    const isDark = theme === 'dark';
    const textMain = 'text-[var(--text-primary)]';
    const textSub = 'text-[var(--text-secondary)]';
    const cardBg = isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-[var(--border)]';

    useEffect(() => {
        if (id) loadSimulation();
    }, [id]);

    const loadSimulation = async () => {
        if (!id) return;
        setIsLoading(true);
        setError(null);
        try {
            const sim = await simulationsService.getOne(id);
            setSimulation(sim);
        } catch (err: any) {
            console.error('Failed to load simulation:', err);
            setError(err.message || 'Failed to load simulation');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRerun = async () => {
        if (!id) return;
        setIsRunning(true);
        setProgress(0);
        setProgressMessage(language === 'es' ? 'Iniciando simulación...' : 'Starting simulation...');

        try {
            await simulationsService.run(id);

            // Simulate progress stages (FTS is fast)
            const stages = [
                { p: 15, msg: language === 'es' ? 'Cargando datos DACAMS...' : 'Loading DACAMS data...' },
                { p: 35, msg: language === 'es' ? 'Calculando escenario S1 (As-Is)...' : 'Calculating S1 (As-Is) scenario...' },
                { p: 55, msg: language === 'es' ? 'Calculando escenario S2 (AMS)...' : 'Calculating S2 (AMS) scenario...' },
                { p: 70, msg: language === 'es' ? 'Calculando escenario S3 (FASTGate)...' : 'Calculating S3 (FASTGate) scenario...' },
                { p: 85, msg: language === 'es' ? 'Calculando escenario S4 (To-Be+)...' : 'Calculating S4 (To-Be+) scenario...' },
                { p: 95, msg: language === 'es' ? 'Generando animación...' : 'Generating animation...' },
            ];

            // Progress through stages
            for (const stage of stages) {
                await new Promise(r => setTimeout(r, 400));
                setProgress(stage.p);
                setProgressMessage(stage.msg);
            }

            // Poll for completion
            const pollForCompletion = async () => {
                const sim = await simulationsService.getOne(id);
                if (sim && (sim.status === 'COMPLETED' || sim.status === 'FAILED')) {
                    setProgress(100);
                    setProgressMessage(language === 'es' ? '¡Completado!' : 'Completed!');
                    setTimeout(() => {
                        setSimulation(sim);
                        setIsRunning(false);
                        setProgress(0);
                    }, 500);
                } else {
                    setTimeout(pollForCompletion, 1000);
                }
            };

            pollForCompletion();
        } catch (err: any) {
            console.error('Failed to run simulation:', err);
            setError(language === 'es'
                ? `Error al ejecutar la simulación: ${err.message || 'Error desconocido'}`
                : `Failed to run simulation: ${err.message || 'Unknown error'}`);
            setIsRunning(false);
            setProgress(0);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'COMPLETED':
                return { icon: CheckCircle2, color: 'text-green-500 bg-green-500/10', text: language === 'es' ? 'Completado' : 'Completed' };
            case 'RUNNING':
                return { icon: Loader2, color: 'text-blue-500 bg-blue-500/10', text: language === 'es' ? 'Ejecutando' : 'Running' };
            case 'FAILED':
                return { icon: AlertCircle, color: 'text-red-500 bg-red-500/10', text: 'Error' };
            default:
                return { icon: Clock, color: 'text-yellow-500 bg-yellow-500/10', text: language === 'es' ? 'Pendiente' : 'Pending' };
        }
    };

    const EnabledOption = ({ enabled, icon: Icon, label, color }: { enabled: boolean; icon: any; label: string; color: string }) => (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${enabled
            ? `${isDark ? 'border-[var(--border)] bg-gray-700/50' : 'border-gray-300 bg-gray-100'} ${textMain}`
            : `${isDark ? 'border-[var(--border)] bg-[var(--surface-raised)]' : 'border-[var(--border)] bg-gray-50'} text-[var(--text-secondary)]`}`}>
            <Icon size={16} className={enabled ? color : ''} />
            <span className="text-sm font-medium">{label}</span>
            {enabled && <CheckCircle2 size={14} className="text-green-500 ml-auto" />}
        </div>
    );

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-emerald-500"></div>
            </div>
        );
    }

    if (!simulation) {
        return (
            <div className="p-8 text-center">
                <p className={textSub}>{language === 'es' ? 'Simulación no encontrada' : 'Simulation not found'}</p>
            </div>
        );
    }

    const statusBadge = getStatusBadge(simulation.status);

    return (
        <div className="p-8 font-sans min-h-full">
            <div className="max-w-6xl mx-auto">

                {/* HEADER */}
                <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-6 border-b ${isDark ? 'border-[#2a2a2a]' : 'border-gray-300'}`}>
                    <div className="flex items-start gap-4">
                        <button
                            onClick={() => navigate('/fts')}
                            className={`p-2 rounded-lg hover:bg-gray-500/10 ${textSub} mt-1`}
                        >
                            <ArrowLeft size={24} />
                        </button>
                        <div>
                            <div className="flex items-center gap-3 mb-1">
                                <Play className="w-7 h-7 text-emerald-500" />
                                <h1 className={`text-2xl font-extrabold ${textMain}`}>
                                    {simulation.name}
                                </h1>
                                <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${statusBadge.color}`}>
                                    <statusBadge.icon size={14} className={simulation.status === 'RUNNING' ? 'animate-spin' : ''} />
                                    {statusBadge.text}
                                </div>
                            </div>
                            <p className={`text-sm ${textSub}`}>{simulation.description}</p>
                            <div className={`flex items-center gap-4 mt-2 text-xs ${textSub}`}>
                                <span className="flex items-center gap-1"><BarChart3 size={12} /> {simulation.project_iata} - {simulation.project_name}</span>
                                <span className="flex items-center gap-1"><Clock size={12} /> {new Date(simulation.created_at).toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() => navigate('/fts/new', { state: { editSimulation: simulation } })}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#e5e5e5] hover:bg-[#d4d4d4] text-gray-700 font-medium border border-gray-300"
                        >
                            <Settings size={18} />
                            {language === 'es' ? 'Editar' : 'Edit'}
                        </button>
                        <button
                            onClick={handleRerun}
                            disabled={isRunning}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E8744A] hover:bg-[#e5a050] text-white font-medium disabled:opacity-50"
                        >
                            <RefreshCw size={18} className={isRunning ? 'animate-spin' : ''} />
                            {language === 'es' ? 'Re-ejecutar' : 'Re-run'}
                        </button>
                        <button
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium"
                        >
                            <Download size={18} />
                            {language === 'es' ? 'Exportar' : 'Export'}
                        </button>
                        <button
                            onClick={() => navigate(`/fts/${id}/animation`)}
                            disabled={simulation.status !== 'COMPLETED'}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F26430] hover:bg-[#d14f1f] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Film size={18} />
                            {language === 'es' ? 'Ver Animación' : 'View Animation'}
                        </button>
                    </div>
                </div>

                {/* ENABLED OPTIONS SUMMARY */}
                <div className={`p-6 rounded-xl border mb-6 ${cardBg}`}>
                    <h3 className={`text-sm font-bold uppercase tracking-wider mb-4 ${textSub}`}>
                        {language === 'es' ? 'Configuración del Escenario' : 'Scenario Configuration'}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <EnabledOption enabled={simulation.ams_enabled} icon={Layers} label="AMS" color="text-emerald-500" />
                        <EnabledOption enabled={simulation.fastgate_enabled} icon={Zap} label="FASTGate" color="text-blue-500" />
                        <EnabledOption enabled={simulation.turnaround_monitoring_enabled} icon={Gauge} label="Turnaround Monitoring Tool" color="text-purple-500" />
                        <EnabledOption enabled={simulation.infra_changes_enabled} icon={Building2} label="Infra" color="text-orange-500" />
                        <EnabledOption enabled={simulation.new_sop_enabled} icon={FileText} label="SOP" color="text-cyan-500" />
                        <EnabledOption enabled={simulation.dacams_improvements_enabled} icon={TrendingUp} label="DACAMS" color="text-rose-500" />
                    </div>
                </div>

                {/* RESULTS TABLE - 4 Scenarios from DACAMS */}
                {/* Error banner (from handleRerun or FAILED status) */}
                {error && (
                    <div className={`p-4 rounded-xl border mb-6 flex items-center gap-3 ${isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <p className={`text-sm flex-1 ${isDark ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
                        <button
                            onClick={() => { setError(null); handleRerun(); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors"
                        >
                            <RefreshCw size={14} />
                            {language === 'es' ? 'Reintentar' : 'Retry'}
                        </button>
                    </div>
                )}

                {simulation.status === 'COMPLETED' && !isRunning ? (
                    <SimulationErrorBoundary onRetry={handleRerun} isDark={isDark}>
                        {/* Peak days context badge */}
                        {(() => {
                            const peakDays: any[] = (simulation.results_kpis as any)?.metadata?.peak_days || [];
                            if (!peakDays.length) return null;
                            return (
                                <div className={`mb-4 px-4 py-2.5 rounded-xl border flex flex-wrap items-center gap-3 text-xs ${isDark ? 'bg-amber-900/15 border-amber-500/25 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                                    <span className="font-bold uppercase tracking-wide">
                                        {language === 'es' ? 'Simulado sobre los 10 días de mayor tráfico' : 'Simulated on the 10 busiest traffic days'}
                                    </span>
                                    <div className="flex flex-wrap gap-1.5">
                                        {peakDays.slice(0, 10).map((d: any, i: number) => (
                                            <span key={d.date} className={`px-2 py-0.5 rounded font-mono ${i === 0 ? (isDark ? 'bg-amber-500/30 text-amber-200' : 'bg-amber-200 text-amber-800') : (isDark ? 'bg-[var(--surface-raised)] text-[var(--text-secondary)]' : 'bg-white text-gray-600 border border-gray-200')}`}>
                                                {d.date} <span className="opacity-60">({d.total})</span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        <SimulationResultsTable results={simulation.results_kpis as SimulationResults} />

                        {/* ROI Economic Impact Panel */}
                        {(() => {
                            const ei = (simulation.results_kpis as any)?.economic_impact;
                            const hasData = ei && typeof ei.total_annual_impact === 'number';
                            return hasData ? (
                                <div className="mt-6">
                                    <ROIPanel
                                        economicImpact={ei as EconomicImpact}
                                        projectName={simulation.project_name}
                                    />
                                </div>
                            ) : null;
                        })()}
                    </SimulationErrorBoundary>
                ) : simulation.status === 'RUNNING' || isRunning ? (
                    <div className={`p-8 rounded-xl border ${cardBg}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <Loader2 className="w-8 h-8 text-[#F26430] animate-spin" />
                                <div>
                                    <h3 className={`text-lg font-bold ${textMain}`}>
                                        {language === 'es' ? 'Simulación en Progreso' : 'Simulation in Progress'}
                                    </h3>
                                    <p className={`text-sm ${textSub}`}>{progressMessage}</p>
                                </div>
                            </div>
                            <span className="text-2xl font-black text-[#F26430]">{progress}%</span>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full h-3 bg-[var(--surface-inset)] rounded-full overflow-hidden">
                            <div
                                className="h-full bg-[#F26430] transition-all duration-500 ease-out rounded-full"
                                style={{ width: `${progress || 2}%` }}
                            />
                        </div>

                        {/* Progress Stages */}
                        <div className="mt-4 grid grid-cols-6 gap-1 text-xs text-center">
                            {['DACAMS', 'S1', 'S2', 'S3', 'S4', 'Anim'].map((stage, i) => (
                                <div
                                    key={stage}
                                    className={`py-1 px-2 rounded ${progress >= (i + 1) * 15
                                        ? 'bg-[#F26430]/20 text-[#F26430]'
                                        : 'bg-gray-700/50 text-[var(--text-tertiary)]'
                                        }`}
                                >
                                    {stage}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : simulation.status === 'FAILED' ? (
                    <div className={`p-12 rounded-xl border text-center ${cardBg}`}>
                        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                        <h3 className={`text-lg font-bold mb-2 ${textMain}`}>
                            {language === 'es' ? 'Simulación Fallida' : 'Simulation Failed'}
                        </h3>
                        <p className={`mb-2 ${textSub}`}>
                            {language === 'es'
                                ? 'La simulación no se completó correctamente.'
                                : 'The simulation did not complete successfully.'}
                        </p>
                        {(simulation as any)?.error_message && (
                            <p className={`text-xs mb-4 font-mono px-4 py-2 rounded-lg inline-block ${isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-600'}`}>
                                {(simulation as any).error_message}
                            </p>
                        )}
                        <div className="mt-4">
                            <button
                                onClick={handleRerun}
                                disabled={isRunning}
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#F26430] hover:bg-[#d14f1f] text-white font-bold transition-colors"
                            >
                                <RefreshCw size={18} />
                                {language === 'es' ? 'Reintentar Simulación' : 'Retry Simulation'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className={`p-12 rounded-xl border text-center ${cardBg}`}>
                        <Clock className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                        <h3 className={`text-lg font-bold mb-2 ${textMain}`}>
                            {language === 'es' ? 'Simulación Pendiente' : 'Simulation Pending'}
                        </h3>
                        <p className={`mb-4 ${textSub}`}>
                            {language === 'es'
                                ? 'Haz clic en "Re-ejecutar" para iniciar la simulación.'
                                : 'Click "Re-run" to start the simulation.'}
                        </p>
                        <button
                            onClick={handleRerun}
                            disabled={isRunning}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold"
                        >
                            <Play size={18} />
                            {language === 'es' ? 'Ejecutar Ahora' : 'Run Now'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SimulationDetailPage;
